import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Eye, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Segmented } from "@/components/admin/Toolbar";
import { adminEmailPreview, adminTestEmail, errorMessage } from "@/lib/api";

const KINDS = [
  { value: "booking", label: "Stadfesting" },
  { value: "event", label: "Arrangør" },
  { value: "approved", label: "Godkjend" },
  { value: "rejected", label: "Avslag" },
  { value: "welcome", label: "Velkomst arrangør" },
];

/** Live preview of the e-mails the house sends (sample data) + «send this one to the board's address». */
export const EmailPreview = ({ disabled }) => {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("booking");
  const { data, isLoading, isError } = useQuery({ queryKey: ["admin-email-preview", kind], queryFn: () => adminEmailPreview(kind), enabled: open, staleTime: 60_000 });
  const send = useMutation({ mutationFn: () => adminTestEmail(kind), onSuccess: (d) => toast.success(`Prøve sendt til ${d.to}`), onError: (e) => toast.error(errorMessage(e, "Kunne ikkje sende")) });
  return (
    <>
      <Button variant="outline" className="h-9 bg-card" onClick={() => setOpen(true)} data-testid="settings-email-preview-button"><Eye className="h-4 w-4" /> Sjå e-postane</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-2xl" data-testid="email-preview-dialog">
          <DialogHeader className="space-y-1 border-b border-border/70 px-5 pb-4 pt-5 text-left">
            <DialogTitle className="font-display text-lg tracking-tight">Slik ser e-postane ut</DialogTitle>
            <DialogDescription>Eksempeldata. Innsendaren får «Stadfesting» med ein gong, og «Godkjend»/«Avslag» når styret svarar.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-3 bg-muted/40 px-5 py-3">
            <Segmented value={kind} onChange={setKind} items={KINDS} size="sm" testId="email-preview-kind" />
            <Button size="sm" variant="outline" className="h-8 bg-card" onClick={() => send.mutate()} disabled={disabled || send.isPending} title={disabled ? "Lagre innstillingane først" : "Sender denne e-posten til varslings-e-posten"} data-testid="email-preview-send-button">
              {send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send prøve til styret
            </Button>
          </div>
          <div className="relative h-[70vh] bg-[#F2EEE6]" data-testid="email-preview-frame-wrap">
            {isLoading && <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>}
            {isError && <div className="absolute inset-0 grid place-items-center text-sm text-destructive" data-testid="email-preview-error">Kunne ikkje hente førehandsvisinga.</div>}
            {data && <iframe title="E-post" srcDoc={data} sandbox="" className="h-full w-full border-0" data-testid="email-preview-frame" />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
